import { Form, Input, InputNumber, Modal, message } from 'antd';
import { useState } from 'react';
import axios from '../config/axios';
import { handleAPIError } from '../utils/helpers';

interface SwapDeviceModalProps {
	visible: boolean;
	onCancel: () => void;
	onSuccess: () => void;
	oldDeviceId: number;
	newDeviceId?: number | null;
}

interface SwapDeviceFormValues {
	old_device_id: number;
	new_device_id: number;
}

const SwapDeviceModal = ({ visible, onCancel, onSuccess, oldDeviceId, newDeviceId }: SwapDeviceModalProps) => {
	const [form] = Form.useForm<SwapDeviceFormValues>();
	const [loading, setLoading] = useState(false);

	const handleSwapDevice = async (values: SwapDeviceFormValues) => {
		setLoading(true);
		try {
			const response = await axios.post(
				'/admin/super/devices/swap',
				{
					old_device_id: values.old_device_id,
					new_device_id: values.new_device_id,
				},
				{
					withCredentials: true,
				},
			);

			// Check if response indicates an error
			if (response.data?.err) {
				message.error(response.data.msg || 'Failed to swap devices');
				return;
			}

			message.success('Device swapped successfully');
			form.resetFields();
			onSuccess();
		} catch (err) {
			const error = err as Error;
			handleAPIError(error);
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		onCancel();
	};

	return (
		<Modal
			title="Swap Device"
			open={visible}
			onCancel={handleCancel}
			onOk={() => form.submit()}
			confirmLoading={loading}
			destroyOnClose
		>
			<Form
				form={form}
				layout="vertical"
				onFinish={handleSwapDevice}
				initialValues={{ old_device_id: oldDeviceId, new_device_id: newDeviceId ?? undefined }}
			>
				<Form.Item
					name="old_device_id"
					label="Old Device ID"
					rules={[{ required: true, message: 'Old device ID is required' }]}
				>
					<Input
						disabled
						placeholder="Current device ID"
					/>
				</Form.Item>

				<Form.Item
					name="new_device_id"
					label="New Device ID"
					rules={[
						{ required: true, message: 'New device ID is required' },
						{ type: 'number', message: 'Please enter a valid device ID' },
					]}
				>
					<InputNumber
						placeholder="Enter new device ID"
						min={1}
						style={{ width: '100%' }}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default SwapDeviceModal;
